import React, {
	useEffect,
	useRef,
	forwardRef,
	useContext,
	createContext,
	useState,
	useCallback,
	useImperativeHandle,
} from "react";
import type { PrimitiveAtom } from "jotai";
import L, { DragEndEvent } from "leaflet";
import { leafletLayer, light, dark, paintRules } from "protomaps";
import { Directions, AvoidedWay, color, midpoint, LatLng } from "./primitives";
import {
	decode,
	edgeAtLocation,
	fetchDirections,
	toValhalla,
} from "./valhalla";
import { useAtom, useSetAtom } from "jotai";
import { avoidsState, costingState, mapStateAtom } from "./state";

const LeafletContext = createContext<L.Map | null>(null);

const icon = L.divIcon({
	iconSize: [30, 30],
	className: "route-icon",
	html: document.querySelector("#svg5")?.outerHTML,
});

const fromIcon = icon;
const toIcon = icon;

type DragEndPayload = {
	from: LatLng;
	to: LatLng;
};

const DirectionsMarker = ({
	direction,
	isFrom,
	onDragEnd,
}: {
	direction: Directions;
	isFrom: boolean;
	onDragEnd: (e: DragEndPayload) => void;
}) => {
	const map = useContext(LeafletContext);

	useEffect(() => {
		if (!map) return;
		const { from, to } = direction;

		function onDragEndHandler(ev: DragEndEvent) {
			const marker = ev.target as L.Marker;
			const coords = marker.getLatLng();
			onDragEnd({
				from: isFrom ? [coords.lat, coords.lng] : direction.from,
				to: !isFrom ? [coords.lat, coords.lng] : direction.to,
			});
		}
		const c = color(direction);
		const title = isFrom ? "from" : "to";
		const marker = L.marker(isFrom ? from : to, {
			icon: isFrom ? fromIcon : toIcon,
			draggable: true,
			title,
		})
			.addEventListener("add", function () {
				// @ts-expect-error private prop
				const icon = (this as L.Marker)._icon as HTMLDivElement;
				icon.style.setProperty("--marker-color", c);
				icon.classList.add("icon-" + title);
			})
			.addTo(map)
			.addEventListener("dragend", onDragEndHandler);
		return () => {
			map?.removeLayer(marker);
		};
	}, [map, isFrom]);
	return null;
};

const DirectionsLine = ({
	direction,
	coords,
	time,
}: {
	direction: Directions;
	coords: LatLng[];
	time: number;
}) => {
	const map = useContext(LeafletContext);

	useEffect(() => {
		if (!map) return;
		const c = color(direction);
		const layer = L.polyline(coords, {
			color: c,
		})
			.bindTooltip(
				function (this: L.Tooltip) {
					// @ts-expect-error what
					const container = this._container as HTMLDivElement;
					container.style.setProperty("--tooltip-color", c);
					return `${(time / 60).toFixed(0)} mins`;
				},
				{
					permanent: true,
				}
			)
			.addTo(map);
		map.fitBounds(layer.getBounds());
		return () => {
			map?.removeLayer(layer);
		};
	}, [map]);
	return null;
};

type CoordsAndTime = {
	coords: LatLng[];
	time: number;
};

const DirectionLayer = ({
	directionsAtom,
}: {
	directionsAtom: PrimitiveAtom<Directions>;
}) => {
	const [direction, updateDirection] = useAtom(directionsAtom);
	const [costing] = useAtom(costingState);
	const [avoids] = useAtom(avoidsState);
	const [coords, setCoords] = useState<CoordsAndTime | null>(null);
	useEffect(() => {
		const fetchDirection = async () => {
			const newDir = await fetchDirections(
				[direction.from, direction.to],
				avoids.map((a) => toValhalla(midpoint(decode(a.polyline)))),
				costing
			);
			setCoords({
				coords: newDir.coords,
				time: newDir.time,
			});
			// updateMap({ zoom: map?.getZoom(), center: latlng });
		};
		fetchDirection().catch(console.error);
	}, [
		direction.from,
		direction.to,
		avoids.length,
		costing.use_bikepaths,
		costing.use_roads,
		costing.use_oneways,
	]);
	return (
		<>
			{coords ? (
				<DirectionsLine
					key={coords.time}
					direction={direction}
					coords={coords.coords}
					time={coords.time}
				/>
			) : null}
			<DirectionsMarker
				key={direction.from.toString()}
				direction={direction}
				isFrom={true}
				onDragEnd={(e) => updateDirection((dir) => ({ ...dir, ...e }))}
			/>
			<DirectionsMarker
				key={direction.to.toString()}
				direction={direction}
				isFrom={false}
				onDragEnd={(e) => updateDirection((dir) => ({ ...dir, ...e }))}
			/>
		</>
	);
};

export { DirectionLayer };

const AvoidedLine = ({ avoid }: { avoid: AvoidedWay }) => {
	const map = useContext(LeafletContext);
	const removeAvoid = useSetAtom(avoidsState);
	useEffect(() => {
		if (!map) return;
		const { polyline, id } = avoid;
		const avoidPolyline = L.polyline(decode(polyline), {
			color: "red",
			bubblingMouseEvents: false,
		})
			.addEventListener("click", (e) => {
				L.DomEvent.stopPropagation(e);
				removeAvoid((prev) => prev.filter((av) => av.id != id));
			})
			.addTo(map);

		return () => {
			map?.removeLayer(avoidPolyline);
		};
	}, [map]);
	return null;
};

export { AvoidedLine };

const MapRef = forwardRef(
	(
		{
			children,
			editingAvoids,
		}: { children: JSX.Element[] | JSX.Element; editingAvoids: boolean },
		ref
	) => {
		const elRef = useRef<HTMLDivElement>(null);
		const [map, setMap] = useState<L.Map | null>(null);
		const [pmtileslayer, setPmtileslayer] = useState<L.Layer | null>(null);
		const [mapState, updateMap] = useAtom(mapStateAtom);
		const { zoom, center } = mapState;
		const updateAvoid = useSetAtom(avoidsState);
		const createAvoidBatch = (action: AvoidedWay[]) =>
			updateAvoid((prev) =>
				Array.from(
					prev
						.concat(action)
						.reduce((p, c) => {
							p.set(c.polyline, c);
							return p;
						}, new Map())
						.values()
				)
			);
		const onAvoidAdd = useCallback(
			async (
				e: L.LeafletEvent & {
					originalEvent: MouseEvent;
					latlng: { lat: number; lng: number };
				}
			) => {
				if (!e.originalEvent.isTrusted) return;

				const { lat, lng } = e.latlng;
				const parsed = await edgeAtLocation([lat, lng]);
				createAvoidBatch(parsed);
			},
			[createAvoidBatch]
		);

		useImperativeHandle(ref, () => map);
		useEffect(() => {
			if (!elRef.current) return;
			const map = L.map(elRef.current);
			map.zoomControl.setPosition("bottomright");
			setMap(map);

			// const p = new PMTiles(
			// 	"https://bigboy-directions.s3.amazonaws.com/tiles.pmtiles"
			// );
			// p.metadata().then(() => {
			// const paint_rules = paintRules(light, "");
			// paint_rules.push({
			// 	dataLayer: "roads",
			// 	symbolizer: new LineSymbolizer({
			// 		color: "red",
			// 		width: exp(1.4, [[9, 3], [12, 4], [17, 8], [20, 22]]),
			// 	}),
			// 	filter: (z: number, f: any) => {
			// 		// debugger;
			// 		return f.props["pmap:kind"] == "major_road";
			// 	},
			// });
			// const view = fallback(
			// 	"https://bigboy-directions.s3.amazonaws.com/tiles.pmtiles",
			// 	"https://api.protomaps.com/tiles/v2/{z}/{x}/{y}.pbf?key=f02c9464c54f4c41",
			// 	map
			// );
			const layer = leafletLayer({
				// xray: true,
				// paint_rules: paint_rules,
				// label_rules: result.label_rules,
				attribution:
					'<a href="https://protomaps.com">Protomaps</a> © <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>',
				// url: `https://api.protomaps.com/tiles/v2/{z}/{x}/{y}.pbf?key=f02c9464c54f4c41`,
				sources: {
					me: {
						url: "https://bigboy-directions.s3.amazonaws.com/tiles.pmtiles",
					},
					"": {
						url: "https://api.protomaps.com/tiles/v2/{z}/{x}/{y}.pbf?key=f02c9464c54f4c41",
					},
				},
			});
			// layer.views = new Map([["", view]]);
			layer.addTo(map);
			setPmtileslayer(layer);
			// });
		}, [elRef]);
		useEffect(() => {
			if (!pmtileslayer) return;
			// @ts-expect-error pmtiles
			pmtileslayer.paint_rules = paintRules(editingAvoids ? dark : light, "");
			// @ts-expect-error pmtiles
			pmtileslayer.clearLayout();
			// @ts-expect-error pmtiles
			pmtileslayer.rerenderTiles();
			if (editingAvoids) {
				map?.dragging.disable();
				map?.addEventListener("click", onAvoidAdd);
			} else {
				map?.dragging.enable();
				map?.removeEventListener("click", onAvoidAdd);
			}
		}, [editingAvoids, pmtileslayer]);
		useEffect(() => {
			function watcher() {
				const center = map?.getCenter();
				const latlng: [number, number] | undefined = center
					? [center?.lat, center.lng]
					: undefined;
				updateMap({ zoom: map?.getZoom(), center: latlng });
			}
			if (!map) return;
			if (zoom) {
				map.setZoom(zoom);
			}
			if (center) {
				map.setView(L.latLng(center[0], center[1]));
			}
			map.addEventListener("zoomend", watcher);
			map.addEventListener("moveend", watcher);
			return () => {
				map.removeEventListener("zoomend", watcher);
				map.removeEventListener("moveend", watcher);
			};
		}, [map]);
		return (
			<div id="map" ref={elRef}>
				<LeafletContext.Provider value={map}>
					{children}
				</LeafletContext.Provider>
			</div>
		);
	}
);

export default MapRef;
